import * as React from 'react';
import PropTypes from 'prop-types';

var GoogleOAuthContext = /*#__PURE__*/React.createContext(null);
/**
 *
 * Custom hook to asynchronously load Google Sign-In script.
 *
 * @param {object} options - Options for loading the script.
 * @param {function} options.onScriptLoadSuccess - Callback function to be executed on successful script load.
 * @param {function} options.onScriptLoadError - Callback function to be executed on script load error.
 * @returns {boolean} Indicates whether the script was loaded successfully.
 */

function useLoadGsiScript(options) {
  var {
    onScriptLoadSuccess,
    onScriptLoadError
  } = options;
  var [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] = React.useState(false);
  var onScriptLoadSuccessRef = React.useRef(onScriptLoadSuccess);
  onScriptLoadSuccessRef.current = onScriptLoadSuccess;
  var onScriptLoadErrorRef = React.useRef(onScriptLoadError);
  onScriptLoadErrorRef.current = onScriptLoadError;
  React.useEffect(() => {
    var scriptTag = document.createElement("script");
    scriptTag.src = "https://accounts.google.com/gsi/client";
    scriptTag.async = true;
    scriptTag.defer = true;

    scriptTag.onload = () => {
      var _onScriptLoadSuccessR;

      setScriptLoadedSuccessfully(true);
      (_onScriptLoadSuccessR = onScriptLoadSuccessRef.current) === null || _onScriptLoadSuccessR === void 0 ? void 0 : _onScriptLoadSuccessR.call(onScriptLoadSuccessRef);
    };

    scriptTag.onerror = () => {
      var _onScriptLoadErrorRef;

      setScriptLoadedSuccessfully(false);
      (_onScriptLoadErrorRef = onScriptLoadErrorRef.current) === null || _onScriptLoadErrorRef === void 0 ? void 0 : _onScriptLoadErrorRef.call(onScriptLoadErrorRef);
    };

    document.body.appendChild(scriptTag);
    return () => {
      document.body.removeChild(scriptTag);
    };
  }, []);
  return scriptLoadedSuccessfully;
}
/**
 *
 * @name GoogleOAuthProvider
 *
 * Provides Google OAuth functionality to its children components.
 *
 * @param {object} props - Props for GoogleOAuthProvider.
 * @param {string} props.clientId - Google OAuth client ID.
 * @param {function} [props.onScriptLoadSuccess] - Callback function to be executed on successful script load.
 * @param {function} [props.onScriptLoadError] - Callback function to be executed on script load error.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} GoogleOAuthProvider component.
 */

function GoogleOAuthProvider(props) {
  var [token, setToken] = React.useState(null); // Load Google Sign-In script

  var scriptLoadedSuccessfully = useLoadGsiScript({
    onScriptLoadSuccess: props.onScriptLoadSuccess,
    onScriptLoadError: props.onScriptLoadError
  });
  /**
   * @name updateToken
   *
   * Update token when session_token changes
   * This is used to update the token when the user registers,
   * but the backend requests additional information. This token
   * should be forwarded alongside with collected data.
   *
   **/

  function updateToken(session_token) {
    setToken(session_token);
  }

  var contextValue = React.useMemo(() => ({
    clientId: props.clientId,
    scriptLoadedSuccessfully,
    updateToken,
    token
  }), [props.clientId, scriptLoadedSuccessfully, updateToken, token]);
  return /*#__PURE__*/React.createElement(GoogleOAuthContext.Provider, {
    value: contextValue
  }, props.children);
}
GoogleOAuthProvider.propTypes = {
  clientId: PropTypes.string.isRequired,
  onScriptLoadSuccess: PropTypes.func,
  onScriptLoadError: PropTypes.func,
  children: PropTypes.node.isRequired
};
/**
 *
 * @name useGoogleOAuth
 *
 * Hook to access Google OAuth context.
 *
 * @returns {object} Context value containing clientId and scriptLoadedSuccessfully.
 * @throws {Error} Throws an error if used outside of GoogleOAuthProvider.
 */

function useGoogleOAuth() {
  var context = React.useContext(GoogleOAuthContext);

  if (!context) {
    throw new Error("Google OAuth components must be used within GoogleOAuthProvider");
  }

  return context;
}

export { GoogleOAuthProvider, useGoogleOAuth, useLoadGsiScript };
